import React, { useState, useRef } from "react";
import { Shell, Section } from "@/components/loomi/Layout";
import { ADMIN_NAV } from "./AdminDashboard";
import { api, BACKEND_URL, formatApiError, copyToClipboard } from "@/lib/api";
import { toast } from "sonner";
import { Upload, FileText, CheckCircle2, XCircle } from "lucide-react";

const TEMPLATE = `type,name,client,survey_url,target_n,cpi,loi,incidence_rate,reward_points,is_public
api,Q1 Consumer Sentiment,Acme Insights,https://survey.example.com/s/abc,500,2.50,12,45,100,false
adhoc,Coffee Preferences,Acme Insights,https://forms.gle/example,200,0.50,5,80,30,true
api,Mobile Usage Study,Acme Insights,https://survey.example.com/s/mobile,300,1.75,10,55,75,false`;

export default function AdminBulkUpload() {
  const [file, setFile] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  const upload = async () => {
    if (!file) { toast.error("Choose a .csv file first"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/projects/upload-csv", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      toast.success(`Created ${data.count} project(s)`);
    } catch (e) { toast.error("Upload failed", { description: formatApiError(e) }); }
    finally { setBusy(false); }
  };

  const copyTemplate = async () => {
    await copyToClipboard(TEMPLATE);
    toast.success("Template copied to clipboard");
  };
  const downloadTemplate = () => {
    const blob = new Blob([TEMPLATE], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "SyncLine_projects_template.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Shell title="Bulk project upload" portalLabel="Admin" nav={ADMIN_NAV}>
      <div className="grid md:grid-cols-3 gap-6">
        <div className="md:col-span-2">
          <Section title="Upload CSV" testId="section-bulk-upload">
            <div className="border-2 border-dashed border-white/10 rounded-xl p-8 text-center bg-black/30" data-testid="bulk-drop-area">
              <Upload className="w-8 h-8 text-mute mx-auto mb-3" />
              <input ref={inputRef} type="file" accept=".csv" onChange={(e) => setFile(e.target.files?.[0])} className="hidden" data-testid="bulk-file-input" />
              <button onClick={() => inputRef.current?.click()} data-testid="bulk-select-file" className="bg-white/5 hover:bg-white/10 border border-white/10 rounded-md px-4 py-2 text-sm">Choose .csv file</button>
              {file && (
                <div className="mt-4 flex items-center justify-center gap-2 text-sm" data-testid="bulk-file-name">
                  <FileText className="w-4 h-4 text-indigo-300" /> {file.name} · {(file.size / 1024).toFixed(1)} KB
                </div>
              )}
              <div className="mt-4">
                <button onClick={upload} disabled={!file || busy} data-testid="bulk-upload-submit" className="bg-indigo-500 hover:bg-indigo-400 disabled:opacity-60 rounded-md px-5 py-2 text-sm font-medium">
                  {busy ? "Uploading…" : "Upload & create projects"}
                </button>
              </div>
            </div>

            {result && (
              <div className="mt-6 space-y-4" data-testid="bulk-upload-result">
                <div className="flex gap-3 items-center text-sm">
                  <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                  <span><span className="font-semibold" data-testid="bulk-created-count">{result.count}</span> project(s) created</span>
                  {result.errors?.length > 0 && (
                    <>
                      <span className="mx-2 text-mute">·</span>
                      <XCircle className="w-5 h-5 text-rose-400" />
                      <span><span className="font-semibold">{result.errors.length}</span> row(s) failed</span>
                    </>
                  )}
                </div>
                {result.created.length > 0 && (
                  <div className="glass rounded-xl p-4 border border-emerald-500/20">
                    <div className="text-[10px] uppercase tracking-widest text-emerald-300 mb-2">Created</div>
                    <ul className="text-sm space-y-1" data-testid="bulk-created-list">
                      {result.created.map((p) => (<li key={p.id} className="flex gap-2"><span className="uppercase text-[10px] tracking-widest text-mute w-12">{p.type}</span>{p.name}</li>))}
                    </ul>
                  </div>
                )}
                {result.errors?.length > 0 && (
                  <div className="glass rounded-xl p-4 border border-rose-500/20">
                    <div className="text-[10px] uppercase tracking-widest text-rose-300 mb-2">Errors</div>
                    <ul className="text-sm space-y-1" data-testid="bulk-errors-list">
                      {result.errors.map((e, i) => (<li key={i} className="font-mono text-xs">Row {e.row}: {e.error}</li>))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </Section>
        </div>
        <Section title="Template" testId="section-template">
          <p className="text-sm text-mute mb-3">CSV columns: <code className="font-mono text-xs">type, name, client, survey_url, target_n, cpi, loi, incidence_rate, reward_points, is_public</code></p>
          <div className="flex gap-2 mb-4">
            <button onClick={downloadTemplate} data-testid="bulk-download-template" className="text-xs bg-white/5 hover:bg-white/10 border border-white/10 rounded-md px-3 py-2">Download template</button>
            <button onClick={copyTemplate} data-testid="bulk-copy-template" className="text-xs bg-white/5 hover:bg-white/10 border border-white/10 rounded-md px-3 py-2">Copy</button>
          </div>
          <pre className="font-mono text-[11px] bg-black/60 border border-white/10 rounded-md p-3 overflow-x-auto whitespace-pre" data-testid="bulk-template-preview">{TEMPLATE}</pre>
        </Section>
      </div>
    </Shell>
  );
}
